import { Eye, EyeOff, ArrowBigUp } from 'lucide-react';
import { forwardRef, useState, type InputHTMLAttributes } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/shared/lib/cn';
import { useCapsLock } from '@/shared/hooks/useCapsLock';
import { Input } from './Input';

export interface PasswordInputProps extends InputHTMLAttributes<HTMLInputElement> {
  /** Caps Lock uyarısını gizler (nadiren gerekir) */
  hideCapsLockHint?: boolean;
}

/**
 * Şifre alanı — göster/gizle düğmesi ve Caps Lock uyarısıyla.
 *
 * İki eksik vardı: yazdığını göremeyen kullanıcı yanlış yazdığını ancak "şifre hatalı"
 * uyarısıyla anlıyordu ve büyük harf kilidi açıkken bunu hiçbir yerde görmüyordu.
 * Kilit uyarısı alanın ALTINDA duruyor, düzeni itmesin diye yer kaplamıyor.
 *
 * `register()` ile gelen `onKeyDown`/`onBlur` ezilmez; kilit dinleyicileri onların
 * üstüne eklenir — yoksa form doğrulaması alanın dokunulduğunu hiç görmezdi.
 */
export const PasswordInput = forwardRef<HTMLInputElement, PasswordInputProps>(
  ({ className, hideCapsLockHint, onKeyDown, onKeyUp, onBlur, onChange, ...props }, ref) => {
    const { t } = useTranslation();
    const [visible, setVisible] = useState(false);
    /*
     * Göz düğmesi alan DOLUYKEN çıkar.
     *
     * Tarayıcının kendi şifre yöneticisi de simgelerini alanın sağ ucuna koyuyor
     * (anahtar, büyük harf uyarısı); boş alanda üçü yan yana gelince şerit kalabalık
     * görünüyordu. Gizlenecek bir şey yokken düğmenin de işlevi yok.
     */
    const [filled, setFilled] = useState(false);
    const { capsLock, capsProps } = useCapsLock();

    return (
      <div className="flex flex-col gap-1">
        <div className="relative">
          <Input
            ref={ref}
            type={visible ? 'text' : 'password'}
            className={cn(filled && 'pr-10', className)}
            onKeyDown={(e) => {
              capsProps.onKeyDown(e);
              onKeyDown?.(e);
            }}
            onKeyUp={(e) => {
              capsProps.onKeyUp(e);
              onKeyUp?.(e);
            }}
            onBlur={(e) => {
              capsProps.onBlur();
              onBlur?.(e);
            }}
            onChange={(e) => {
              setFilled(e.target.value.length > 0);
              onChange?.(e);
            }}
            {...props}
          />
          {filled ? (
            <button
              type="button"
              // Odak sırasını bozmaz: klavyeyle ilerleyen kullanıcı şifreden doğrudan
              // giriş düğmesine geçer, arada bir göz simgesine takılmaz.
              tabIndex={-1}
              onClick={() => setVisible((v) => !v)}
              aria-label={visible ? t('auth.hidePassword') : t('auth.showPassword')}
              title={visible ? t('auth.hidePassword') : t('auth.showPassword')}
              className="text-fg-subtle hover:bg-bg-hover hover:text-fg absolute top-1/2 right-1.5 flex h-7 w-7 -translate-y-1/2 [animation:octo-fade-up_.15s_ease-out] items-center justify-center rounded-md transition-colors"
            >
              {visible ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          ) : null}
        </div>
        {!hideCapsLockHint && capsLock ? (
          <p
            role="status"
            className="text-warning flex [animation:octo-fade-up_.18s_ease-out] items-center gap-1 text-xs"
          >
            <ArrowBigUp className="h-3.5 w-3.5" />
            {t('auth.capsLock')}
          </p>
        ) : null}
      </div>
    );
  },
);
PasswordInput.displayName = 'PasswordInput';
